import * as React from 'react';
import Link from 'next/link';
import styled from 'styled-components';

import { Translate } from '@packages/i18n';
import { SubCategories, MainNavigationItems, NavigationItem, CategoryLinkItem } from '@packages/common';
import { Icon, themeGet } from '@packages/ui';
import ChevronRightIcon from '../../@packages/ui/components/icons/svgs/chevronRight';
import ChevronLeftIcon from '../../@packages/ui/components/icons/svgs/chevronLeft';

import { NavigationLi, NavigationUl } from './MobileNavigationStyleComponents';

const IconContainer = styled.span`
  width: 16px;
  height: 16px;
  > svg {
    fill: ${themeGet.colors('sandDune')};
    width: inherit;
    height: inherit;
  }
`;

const ItemIconWithSpinner: React.FC<{ showSpinner: boolean }> = ({ showSpinner }) => {
  return showSpinner ? (
    <Icon name="spinner" color="sandDune" width="16px" />
  ) : (
    <IconContainer>
      <ChevronRightIcon />
    </IconContainer>
  );
};

const CategoryLevel = styled.div`
  display: flex;
  width: calc(100% / 4);
`;

interface MobileNavigationCategoryLevelBaseProps {
  handleItemClick: (item: NavigationItem | CategoryLinkItem) => void;
  loadingItemUrl: string | null;
}

interface MobileNavigationCategoryLevelProps extends MobileNavigationCategoryLevelBaseProps {
  handleGoBack?: VoidFunction;
  subCategories: SubCategories;
}

export const MobileNavigationCategoryLevel: React.FC<MobileNavigationCategoryLevelProps> = ({
  handleGoBack,
  subCategories,
  handleItemClick,
  loadingItemUrl,
}) => {
  const { parents, items } = subCategories;
  const parent = parents.length > 0 ? parents[0] : null;

  return (
    <CategoryLevel>
      <NavigationUl>
        <NavigationLi onClick={handleGoBack} data-testid="mobile-navigation-category-level:go-back-button">
          <IconContainer>
            <ChevronLeftIcon />
          </IconContainer>
          <Translate translation="navigateBack"></Translate>
        </NavigationLi>
        {parent?.url ? (
          <NavigationLi>
            <Link prefetch={false} href={parent.url}>
              <a href={parent.url}>
                <Translate
                  translation={{
                    key: 'allInCategory',
                    values: {
                      title: parent.shortTitle,
                    },
                  }}
                />
              </a>
            </Link>
          </NavigationLi>
        ) : null}
        {items.map((item) => (
          <NavigationLi
            onClick={() => handleItemClick(item)}
            key={item.shortTitle}
            data-testid="mobile-navigation-category-level:navigation-item"
          >
            {item.hasChildren ? (
              <>
                {item.shortTitle}
                <ItemIconWithSpinner showSpinner={item.url === loadingItemUrl} />
              </>
            ) : (
              <Link prefetch={false} href={item.url}>
                <a href={item.url}>{item.shortTitle}</a>
              </Link>
            )}
          </NavigationLi>
        ))}
      </NavigationUl>
    </CategoryLevel>
  );
};

interface MobileNavigationMainCategoryLevelProps extends MobileNavigationCategoryLevelBaseProps {
  main: MainNavigationItems['main'];
}

export const MobileNavigationMainCategoryLevel: React.FC<MobileNavigationMainCategoryLevelProps> = ({
  main,
  handleItemClick,
  loadingItemUrl,
}) => {
  return (
    <CategoryLevel>
      <NavigationUl>
        {main.map((item) => (
          <NavigationLi
            onClick={() => handleItemClick(item)}
            key={item.shortTitle}
            data-testid="mobile-navigation-main-category-level:main-category-item"
          >
            {item.shortTitle}
            <ItemIconWithSpinner showSpinner={item.url === loadingItemUrl} />
          </NavigationLi>
        ))}
      </NavigationUl>
    </CategoryLevel>
  );
};
